import { apiClient } from '@lib/api/client';
import type { LoginRequest, LoginResponse, User } from '../types/auth.types';

/**
 * Authentication Service
 * Communicates with the BFF (Backend For Frontend) middleware
 * The BFF handles communication with Aludra API and filters sensitive data
 */
export const authService = {
  /**
   * Login user through BFF middleware
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ user: User }>('/auth/me');
    return response.data.user;
  },

  /**
   * Refresh access token
   * Refresh token is automatically sent via HTTP-only cookie
   */
  async refreshToken(): Promise<{ accessToken: string }> {
    const response = await apiClient.post<{ accessToken: string }>('/auth/refresh');
    return response.data;
  },

  /**
   * Validate current token
   */
  async validateToken(): Promise<{ valid: boolean }> {
    const response = await apiClient.post<{ valid: boolean }>('/auth/validate');
    return response.data;
  },

  /**
   * Forgot password
   * Send password reset email to user
   */
  async forgotPassword(
    email: string,
    tenant: string,
    tenantUrl: string,
    titleButton?: string
  ): Promise<{ message: string; resetToken: string | null }> {
    const response = await apiClient.post<{ message: string; resetToken: string | null }>(
      '/auth/forgot-password',
      {
        email,
        tenant,
        tenantUrl,
        titleButton,
      }
    );
    return response.data;
  },

  /**
   * Reset password with verification code.
   *
   * El `resetToken` viene de forgotPassword y lleva asociados el tenant y el
   * userId del lado del servidor. El cliente ya no los conoce: antes había que
   * pedirlos a /auth/get-user-by-email, un endpoint público que devolvía
   * también el PasswordHash de la cuenta.
   */
  async resetPassword(
    resetToken: string,
    password: string,
    confirmPassword: string,
    code: string
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      resetToken,
      password,
      confirmPassword,
      code,
    });
    return response.data;
  },
};
